import { useAuth } from "common/hooks/useAuth";

import { useCallback, useEffect, useId, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import BlockFormContainer, {
  BlockFormFieldsContainer,
  BlockFormTitleContainer,
} from "layouts/BlockFormLayout";

import axios from "axios";
import LoadingSpinner from "common/components/LoadingSpinner";
import Modal from "common/components/Modal";
import CleanVerbatimsForm from "common/containers/Forms/Blocks/CleanVerbatimsForm";
import { BACKEND_URL_CONFIG } from "config";
import { ERRORS } from "errors";
import AddParametersFilePage from "pages/AddParametersFilePage";
import { toast } from "react-toastify";

export default function EditCleanVerbatims() {
  // Contexts
  const { sectionType, projectId, versionId, subBlockType, blockId } =
    useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // States
  const [needAdvancedOptions, setNeedAdvancedOptions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);
  const [blockInformation, setBlockInformation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [availableColumnsInfo, setAvailableColumnsInfo] = useState(null);

  // Refs
  const formIdRef = useId();

  const fetchAvailableColumns = useCallback(async () => {
    const url = BACKEND_URL_CONFIG.getAvailableColumnsInfo.replace(
      "{versionId}",
      versionId
    );
    try {
      const response = await axios.get(url, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      if (response.status === 200) {
        setAvailableColumnsInfo(response.data);
      }
    } catch (error) {
      toast.error(ERRORS.error_130);
    }
  }, [currentUser, versionId]);

  const fetchBlockInformation = useCallback(async () => {
    const url = BACKEND_URL_CONFIG.getBlockInformation
      .replace("{versionId}", versionId)
      .replace("{sectionId}", sectionType)
      .replace("{blockId}", blockId);
    try {
      const response = await axios.get(url, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
      });
      if (response.status === 200) {
        setBlockInformation(response.data);
      }
    } catch (error) {
      if (error.response.status === 404) {
        toast.error("Le bloc n'existe pas.");
        navigate(
          `/projects/${projectId}/version/${versionId}/run/${sectionType}`
        );
        return;
      }
      toast.error(ERRORS.error_139);
    }
  }, [currentUser, versionId, sectionType, blockId, projectId, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchBlockInformation(), fetchAvailableColumns()]);
      setLoading(false);
    };
    fetchData();
  }, [fetchBlockInformation, fetchAvailableColumns]);

  useEffect(() => {
    if (formData) {
      const payload = {
        section_type: sectionType,
        version_id: versionId,
        block_id: blockId,
        project_id: projectId,
        user_id: currentUser.uid,
        new_values: {
          name: formData.blockDisplayName,
          parameters: formData.parameters,
        },
      };

      updateBlock(payload);
    }
  }, [formData]);

  const updateBlock = async (payload) => {
    setLoading(true);
    try {
      const response = await axios.put(
        BACKEND_URL_CONFIG.updateBlock,
        payload,
        {
          headers: {
            "X-KEY": currentUser.accessToken,
          },
        }
      );
      if (response.status === 200) {
        toast.success("Votre block a été modifié");
        navigate(
          `/projects/${projectId}/version/${versionId}/show/${sectionType}/${blockInformation?.type}`
        );
      }
    } catch (error) {
      if (error.response.status === 403) {
        toast.error(ERRORS.error_129);
      } else {
        toast.error(ERRORS.error_138);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <BlockFormContainer>
      <Modal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        customStyle={{ width: "900px" }}
      >
        <AddParametersFilePage blockInformation={blockInformation} />
      </Modal>
      <BlockFormTitleContainer
        title={`Modifier le bloc : ${blockInformation?.name ?? ""}`}
        formId={formIdRef}
        loading={loading}
        needAdvancedOptions={needAdvancedOptions}
        isSubmitting={isSubmitting}
        setNeedAdvancedOptions={setNeedAdvancedOptions}
        setIsModalOpen={setIsModalOpen}
        isModalOpen={isModalOpen}
      />
      <BlockFormFieldsContainer>
        {loading ? (
          <LoadingSpinner iconCustomStyle={{ fontSize: "25px" }} />
        ) : (
          <CleanVerbatimsForm
            block={blockInformation}
            formId={formIdRef}
            setFormData={setFormData}
            needAdvancedOptions={needAdvancedOptions}
            defaultBlockDisplayName={subBlockType}
            setIsSubmitting={setIsSubmitting}
            availableColumnsInfo={availableColumnsInfo}
          />
        )}
      </BlockFormFieldsContainer>
    </BlockFormContainer>
  );
}
